import { API_URL } from "../config/api";
import { getToken, clearToken } from "./auth";

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// The app listens for this so an expired token bounces the user to the login
// screen with an explanation, rather than failing every button silently.
const sessionExpiredListeners = new Set();

export function onSessionExpired(listener) {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function notifySessionExpired() {
  sessionExpiredListeners.forEach((listener) => listener());
}

// The API is not consistent about which key carries the human-readable reason:
// rate limiting returns { error }, auth failures return { message }, and
// express-validator returns { errors: [{ msg }] }. Read all three.
function messageFrom(data, fallback) {
  return (
    data?.error ||
    data?.message ||
    data?.errors?.[0]?.msg ||
    fallback
  );
}

export async function apiRequest(path, options = {}) {
  const { method = "GET", body, auth = false, signal } = options;

  const headers = {};
  let payload = body;

  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  // FormData sets its own multipart boundary — never override it.
  if (body && !(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(`${API_URL}${path}`, { method, headers, body: payload, signal });
  } catch (err) {
    // An aborted request is a cancellation, not a failure — let callers ignore it.
    if (err.name === "AbortError") throw err;
    throw new ApiError("Can't reach the server. Check your connection and try again.", 0);
  }

  const data = await response.json().catch(() => null);

  // Only treat 401 as an expired session on authenticated calls. On the login
  // form a 401 just means the credentials were wrong.
  if (response.status === 401 && auth) {
    clearToken();
    notifySessionExpired();
    throw new ApiError("Your session has expired. Please sign in again.", 401);
  }

  if (!response.ok) {
    throw new ApiError(messageFrom(data, `Request failed (${response.status})`), response.status);
  }

  return data;
}

// Uploads need real progress events, which fetch can't report. XHR can, so this
// mirrors apiRequest's auth and error handling on top of it.
export function uploadWithProgress(path, formData, { onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}${path}`, true);

    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    const abort = () => xhr.abort();
    if (signal) {
      if (signal.aborted) return reject(new DOMException("Aborted", "AbortError"));
      signal.addEventListener("abort", abort);
    }

    const cleanup = () => signal?.removeEventListener("abort", abort);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      cleanup();

      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        // Non-JSON body; messageFrom falls back below.
      }

      if (xhr.status === 401) {
        clearToken();
        notifySessionExpired();
        return reject(new ApiError("Your session has expired. Please sign in again.", 401));
      }

      if (xhr.status >= 200 && xhr.status < 300) return resolve(data);

      reject(new ApiError(messageFrom(data, `Upload failed (${xhr.status})`), xhr.status));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new ApiError("Upload failed. Check your connection and try again.", 0));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };

    xhr.send(formData);
  });
}

export function isAbortError(err) {
  return err?.name === "AbortError";
}
